import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './AdminInvoicesPage.module.scss';
import { adminUnifiedActivity } from '../../../redux/actions/payment/adminUnifiedActivity';
import { adminInvoiceStats } from '../../../redux/actions/payment/adminInvoiceStats';

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'paid', label: 'Оплачен' },
  { value: 'pending', label: 'Ожидает' },
  { value: 'cancelled', label: 'Отменён' },
  { value: 'refunded', label: 'Возврат' },
  { value: 'partially_refunded', label: 'Частичный возврат' },
  { value: 'expired', label: 'Истёк' },
];

const STATUS_LABELS = {
  paid: 'Оплачен',
  pending: 'Ожидает',
  cancelled: 'Отменён',
  refunded: 'Возврат',
  partially_refunded: 'Частичный возврат',
  expired: 'Истёк',
};

const RECORD_TYPE_OPTIONS = [
  { value: '', label: 'Все записи' },
  { value: 'invoice', label: 'Инвойсы' },
  { value: 'profile_change', label: 'Изменения профилей' },
];

const END_DATE_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'ending_soon', label: 'Истекает через 7 дней' },
];

const COLUMNS = [
  { key: 'date', label: 'Дата' },
  { key: 'record_type', label: 'Тип' },
  { key: 'phone', label: 'Телефон' },
  { key: 'user_email', label: 'Email' },
  { key: 'client_name', label: 'Клиент' },
  { key: 'kaspi_invoice_id', label: 'Инвойс ID' },
  { key: 'description', label: 'Описание' },
  { key: 'tariff_name', label: 'Тариф' },
  { key: 'profile_end_date', label: 'Конец тарифа' },
  { key: 'amount', label: 'Сумма' },
  { key: 'discount_sum', label: 'Скидка' },
  { key: 'total', label: 'Итого' },
  { key: 'total_refunded', label: 'Возврат ₸' },
  { key: 'status', label: 'Статус платежа' },
  { key: 'paid_at', label: 'Оплачен' },
  { key: 'history_type', label: 'Действие' },
  { key: 'changed_by', label: 'Кто изменил' },
  { key: 'changed_fields', label: 'Изменённые поля' },
];

const DEFAULT_VISIBLE = Object.fromEntries(COLUMNS.map((c) => [c.key, true]));

const HISTORY_TYPE_LABELS = { '+': 'Создан', '~': 'Изменён', '-': 'Удалён' };
const HISTORY_TYPE_COLORS = { '+': '#28a745', '~': '#f0a500', '-': '#dc3545' };
const RECORD_TYPE_COLORS = { invoice: '#5f63f2', profile_change: '#17a2b8' };
const RECORD_TYPE_LABELS = { invoice: 'Инвойс', profile_change: 'Изм. профиля' };

const PER_PAGE = 30;

const fmt = (n) =>
  n != null
    ? Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '0';

const formatDate = (str) => {
  if (!str) return '—';
  return new Date(str).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDateOnly = (str) => {
  if (!str) return '—';
  const [y, m, d] = str.split('T')[0].split('-');
  return `${d}.${m}.${y}`;
};

const getEndDateMeta = (dateStr) => {
  if (!dateStr) return { cls: '', label: '—' };
  const end = new Date(dateStr.split('T')[0]);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffDays = Math.round((end - now) / 86400000);
  if (diffDays < 0) return { cls: styles.endDateExpired, label: formatDateOnly(dateStr) };
  if (diffDays <= 7) return { cls: styles.endDateSoon, label: formatDateOnly(dateStr) };
  return { cls: styles.endDateActive, label: formatDateOnly(dateStr) };
};

// ── Main Component ─────────────────────────────────────────────────────────────

const AdminInvoicesPage = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.userProfile.data);
  const { data, loading, error } = useSelector((state) => state.payment.adminUnifiedActivity);
  const { data: statsData } = useSelector((state) => state.payment.adminInvoiceStats);

  const [filters, setFilters] = useState({
    search: '',
    record_type: '',
    changed_by: '',
    end_date_filter: '',
    date_from: '',
    date_to: '',
    sort_by: 'date',
    sort_order: 'desc',
  });
  const [page, setPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [colDropOpen, setColDropOpen] = useState(false);
  const colDropRef = useRef(null);

  const buildParams = useCallback((f, p) => {
    const params = { per_page: PER_PAGE, page: p, sort_by: f.sort_by, sort_order: f.sort_order };
    const statsParams = {};
    if (f.search) { params.search = f.search; statsParams.search = f.search; }
    if (f.record_type) params.record_type = f.record_type;
    if (f.changed_by) params.changed_by = f.changed_by;
    if (f.end_date_filter) { params.end_date_filter = f.end_date_filter; statsParams.end_date_filter = f.end_date_filter; }
    if (f.date_from) { params.date_from = f.date_from; statsParams.date_from = f.date_from; }
    if (f.date_to) { params.date_to = f.date_to; statsParams.date_to = f.date_to; }
    return { params, statsParams };
  }, []);

  const fetchData = useCallback(
    (currentFilters = filters, currentPage = page) => {
      const { params, statsParams } = buildParams(currentFilters, currentPage);
      dispatch(adminUnifiedActivity(params));
      dispatch(adminInvoiceStats(statsParams));
    },
    [dispatch, filters, page, buildParams]
  );

  useEffect(() => {
    if (profile?.is_admin) {
      fetchData(filters, page);
    }
  }, [profile, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (colDropRef.current && !colDropRef.current.contains(e.target)) {
        setColDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData(filters, 1);
  };

  const handleReset = () => {
    const reset = {
      search: '', record_type: '', changed_by: '', end_date_filter: '',
      date_from: '', date_to: '', sort_by: 'date', sort_order: 'desc',
    };
    setFilters(reset);
    setPage(1);
    dispatch(adminUnifiedActivity({ per_page: PER_PAGE, page: 1, sort_by: 'date', sort_order: 'desc' }));
    dispatch(adminInvoiceStats({}));
  };

  const handleColumnSort = (key) => {
    const newOrder = filters.sort_by === key && filters.sort_order === 'asc' ? 'desc' : 'asc';
    const newFilters = { ...filters, sort_by: key, sort_order: newOrder };
    setFilters(newFilters);
    setPage(1);
    fetchData(newFilters, 1);
  };

  const toggleColumn = (key) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleAllColumns = (val) =>
    setVisibleColumns(Object.fromEntries(COLUMNS.map((c) => [c.key, val])));

  const records = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  if (!profile?.is_admin) {
    return <div className={styles.errorContainer}>Нет доступа.</div>;
  }

  const stats = {
    pending: statsData?.pending ?? 0,
    paid: statsData?.paid ?? 0,
    expired: statsData?.expired ?? 0,
    cancelled: statsData?.cancelled ?? 0,
    refunded: statsData?.refunded ?? 0,
    active: statsData?.active ?? 0,
    endingSoon: statsData?.ending_soon ?? 0,
    totalAmount: statsData?.total_amount ?? 0,
    totalPaid: statsData?.total_paid ?? 0,
    totalRefund: statsData?.total_refunded_amount ?? 0,
  };

  const statTotal = Math.max(statsData?.total ?? 1, 1);

  const BAR_SEGMENTS = [
    { label: 'Оплачен', color: '#28a745', count: stats.paid },
    { label: 'Ожидает', color: '#f0a500', count: stats.pending },
    { label: 'Отменён', color: '#dc3545', count: stats.cancelled },
    { label: 'Истёк', color: '#6c757d', count: stats.expired },
    { label: 'Возврат', color: '#0066cc', count: stats.refunded },
    { label: 'Истекает скоро', color: '#fd7e14', count: stats.endingSoon },
  ];

  const COUNT_CHIPS = [
    { label: 'Всего', value: statsData?.total ?? '—', color: '#5f63f2' },
    { label: 'Активные', value: stats.active, color: '#17a2b8' },
    { label: 'Оплачено', value: stats.paid, color: '#28a745' },
    { label: 'Ожидает', value: stats.pending, color: '#f0a500' },
    { label: 'Отменён', value: stats.cancelled, color: '#dc3545' },
    { label: 'Истёк', value: stats.expired, color: '#6c757d' },
    { label: 'Возврат', value: stats.refunded, color: '#0066cc' },
    { label: 'Истекает скоро', value: stats.endingSoon, color: '#fd7e14' },
  ];

  const MONEY_CARDS = [
    { label: 'Общая сумма', value: `${fmt(stats.totalAmount)} ₸`, sub: 'все заказы по фильтру', icon: '💰', color: '#5f63f2' },
    { label: 'Оплачено', value: `${fmt(stats.totalPaid)} ₸`, sub: 'оплаченные заказы', icon: '💳', color: '#28a745' },
    { label: 'Возврат', value: `${fmt(stats.totalRefund)} ₸`, sub: 'возвращённые средства', icon: '↩', color: '#0066cc' },
  ];

  const activeFilterCount = [
    filters.search, filters.record_type, filters.changed_by, filters.end_date_filter,
    filters.date_from, filters.date_to,
  ].filter(Boolean).length;

  const visibleCols = COLUMNS.filter((c) => visibleColumns[c.key]);
  const allVisible = COLUMNS.every((c) => visibleColumns[c.key]);
  const noneVisible = COLUMNS.every((c) => !visibleColumns[c.key]);

  const renderCell = (col, rec) => {
    const dash = <td key={col.key}>—</td>;

    switch (col.key) {
      case 'date':
        return <td key={col.key}>{formatDate(rec.date)}</td>;

      case 'record_type': {
        const color = RECORD_TYPE_COLORS[rec.record_type] || '#888';
        return (
          <td key={col.key}>
            <span
              className={styles.badge}
              style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
            >
              {RECORD_TYPE_LABELS[rec.record_type] || rec.record_type}
            </span>
          </td>
        );
      }

      case 'history_type':
        if (!rec.history_type) return dash;
        return (
          <td key={col.key}>
            <span
              className={styles.badge}
              style={{
                background: `${HISTORY_TYPE_COLORS[rec.history_type]}22`,
                color: HISTORY_TYPE_COLORS[rec.history_type],
                border: `1px solid ${HISTORY_TYPE_COLORS[rec.history_type]}44`,
              }}
            >
              {HISTORY_TYPE_LABELS[rec.history_type] || rec.history_type}
            </span>
          </td>
        );

      case 'status':
        if (!rec.status) return dash;
        return (
          <td key={col.key}>
            <span className={`${styles.badge} ${styles[rec.status] || ''}`}>
              {STATUS_LABELS[rec.status] || rec.status}
            </span>
          </td>
        );

      case 'profile_end_date': {
        const meta = getEndDateMeta(rec.profile_end_date);
        return (
          <td key={col.key}>
            {rec.profile_end_date
              ? <span className={`${styles.endDateBadge} ${meta.cls}`}>{meta.label}</span>
              : '—'}
          </td>
        );
      }

      case 'amount':
        return <td key={col.key}>{rec.amount != null ? `${rec.amount} ₸` : '—'}</td>;
      case 'discount_sum':
        return <td key={col.key}>{rec.discount_sum != null ? `${rec.discount_sum} ₸` : '—'}</td>;
      case 'total':
        return <td key={col.key}>{rec.total != null ? `${rec.total} ₸` : '—'}</td>;
      case 'total_refunded':
        return <td key={col.key}>{rec.total_refunded != null ? `${rec.total_refunded} ₸` : '—'}</td>;

      case 'paid_at':
        return <td key={col.key}>{formatDate(rec.paid_at)}</td>;

      case 'changed_by':
        return (
          <td key={col.key}>
            <div className={styles.adminCell}>
              <span className={styles.adminName}>{rec.changed_by || '—'}</span>
              {rec.changed_by_email && rec.changed_by_email !== rec.changed_by && (
                <span className={styles.adminEmail}>{rec.changed_by_email}</span>
              )}
            </div>
          </td>
        );

      case 'changed_fields':
        if (!rec.changed_fields || rec.changed_fields.length === 0) {
          return (
            <td key={col.key}>
              <span style={{ color: '#bbb', fontSize: 12 }}>
                {rec.record_type === 'profile_change' && rec.history_type === '+'
                  ? 'Новая запись'
                  : '—'}
              </span>
            </td>
          );
        }
        return (
          <td key={col.key}>
            <div className={styles.changedFields}>
              {rec.changed_fields.map((cf, i) => (
                <div key={i} className={styles.changedField}>
                  <span className={styles.fieldName}>{cf.field}:</span>
                  {cf.old !== null && cf.old !== undefined && (
                    <span className={styles.fieldOld}>{cf.old}</span>
                  )}
                  <span className={styles.fieldArrow}>→</span>
                  <span className={styles.fieldNew}>{cf.new ?? '—'}</span>
                </div>
              ))}
            </div>
          </td>
        );

      default:
        return <td key={col.key}>{rec[col.key] || '—'}</td>;
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Активность — Инвойсы и изменения профилей</h1>

      {/* Stats */}
      <div className={styles.statsSection}>
        <div className={styles.statusBar} title="Распределение статусов инвойсов">
          {BAR_SEGMENTS.map((seg) => (
            <div
              key={seg.label}
              className={styles.statusBarSegment}
              style={{ width: `${(seg.count / statTotal) * 100}%`, background: seg.color }}
              title={`${seg.label}: ${seg.count}`}
            />
          ))}
        </div>

        <div className={styles.countGrid}>
          {COUNT_CHIPS.map((chip) => (
            <div key={chip.label} className={styles.countChip} style={{ '--chip-color': chip.color }}>
              <div className={styles.countChipValue}>
                {statsData ? chip.value : <span className={styles.skeleton} />}
              </div>
              <div className={styles.countChipLabel}>{chip.label}</div>
            </div>
          ))}
        </div>

        <div className={styles.moneyGrid}>
          {MONEY_CARDS.map((card) => (
            <div key={card.label} className={styles.moneyCard} style={{ '--money-color': card.color }}>
              <div className={styles.moneyCardHeader}>
                <span className={styles.moneyCardIcon}>{card.icon}</span>
                <span className={styles.moneyCardLabel}>{card.label}</span>
              </div>
              <div className={styles.moneyCardValue}>
                {statsData ? card.value : <span className={styles.skeleton} style={{ width: '120px', height: '28px' }} />}
              </div>
              <div className={styles.moneyCardSub}>{card.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filterPanel}>
        <div className={styles.filterPanelHeader}>
          <div className={styles.filterPanelTitle}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Фильтры
            {activeFilterCount > 0 && (
              <span className={styles.activeFilterBadge}>{activeFilterCount}</span>
            )}
          </div>
          <div className={styles.filterPanelTools}>
            <div className={styles.colPickerWrap} ref={colDropRef}>
              <button type="button" className={styles.colPickerBtn} onClick={() => setColDropOpen((v) => !v)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                Столбцы
                <span className={styles.colPickerCount}>
                  {COLUMNS.filter((c) => visibleColumns[c.key]).length}/{COLUMNS.length}
                </span>
              </button>
              {colDropOpen && (
                <div className={styles.colDropdown}>
                  <div className={styles.colDropHeader}>
                    <span>Видимые столбцы</span>
                    <div className={styles.colDropActions}>
                      <button type="button" onClick={() => toggleAllColumns(true)} disabled={allVisible}>Все</button>
                      <button type="button" onClick={() => toggleAllColumns(false)} disabled={noneVisible}>Сброс</button>
                    </div>
                  </div>
                  <div className={styles.colList}>
                    {COLUMNS.map((col) => (
                      <label key={col.key} className={styles.colItem}>
                        <input type="checkbox" checked={!!visibleColumns[col.key]} onChange={() => toggleColumn(col.key)} />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="button" className={styles.resetBtn} onClick={handleReset} disabled={loading}>
              Сбросить
            </button>
          </div>
        </div>

        <form onSubmit={handleSearch}>
          <div className={styles.filterGrid}>
            <div className={`${styles.filterGroup} ${styles.filterGroupSearch}`}>
              <label>Поиск</label>
              <div className={styles.searchInputWrap}>
                <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  type="text"
                  placeholder="Телефон, email, инвойс ID..."
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                />
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label>Тип записи</label>
              <select value={filters.record_type} onChange={(e) => setFilters((f) => ({ ...f, record_type: e.target.value }))}>
                {RECORD_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Кто изменил</label>
              <input
                type="text"
                placeholder="Имя или email администратора..."
                value={filters.changed_by}
                onChange={(e) => setFilters((f) => ({ ...f, changed_by: e.target.value }))}
              />
            </div>

            <div className={styles.filterGroup}>
              <label>Конец тарифа</label>
              <select value={filters.end_date_filter} onChange={(e) => setFilters((f) => ({ ...f, end_date_filter: e.target.value }))}>
                {END_DATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Дата от</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} />
            </div>

            <div className={styles.filterGroup}>
              <label>Дата до</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} />
            </div>
          </div>

          <div className={styles.filterFooter}>
            <button type="submit" className={styles.applyBtn} disabled={loading}>
              {loading ? 'Загрузка...' : 'Применить фильтры'}
            </button>
          </div>
        </form>
      </div>

      {loading && <div className={styles.loadingContainer}>Загрузка...</div>}

      {!loading && error && (
        <div className={styles.errorContainer}>{error?.error || 'Ошибка загрузки данных'}</div>
      )}

      {!loading && !error && records.length === 0 && (
        <div className={styles.emptyContainer}>Записи не найдены</div>
      )}

      {!loading && records.length > 0 && (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {visibleCols.map((col) => {
                    const isActive = filters.sort_by === col.key;
                    return (
                      <th
                        key={col.key}
                        className={styles.sortableHeader}
                        onClick={() => handleColumnSort(col.key)}
                        title={`Сортировать по: ${col.label}`}
                      >
                        {col.label}
                        <span className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}>
                          {isActive ? (filters.sort_order === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    {visibleCols.map((col) => renderCell(col, rec))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <span>Всего записей: {total}</span>
            <div className={styles.paginationButtons}>
              <button
                className={styles.pageButton}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                ‹ Назад
              </button>
              <button className={`${styles.pageButton} ${styles.active}`}>{page}</button>
              <button
                className={styles.pageButton}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Вперёд ›
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminInvoicesPage;
